import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FaChevronRight } from "react-icons/fa";
import ProductCard from "../components/ProductCard";
import { getALLproducts } from "../services/productService";
import { getcategorylist } from "../services/categoryService";

const IMG_BASE =
  import.meta.env.VITE_IMG_URL ?? "http://localhost:8000/storage";

const mapApiProduct = (p) => {
  const price = 20 + (p.name.charCodeAt(0) % 50);
  const oldPrice = price + 10;
  const primary = p.images?.find((img) => img.is_primary) || p.images?.[0];
  const img1 = primary
    ? primary.image.startsWith("http")
      ? primary.image
      : `${IMG_BASE}/${primary.image}`
    : "/img/product/placeholder.jpg";
  const second = p.images?.length > 1 ? p.images[1] : primary;
  const img2 = second
    ? second.image.startsWith("http")
      ? second.image
      : `${IMG_BASE}/${second.image}`
    : img1;
  const badge =
    p.name.charCodeAt(1) % 3 === 0
      ? ["Sale"]
      : p.name.charCodeAt(1) % 3 === 1
        ? ["Sale", "New"]
        : [];
  return {
    id: p.product_id,
    product_id: p.product_id,
    name: p.name,
    category: p.category?.name || "",
    category_id: p.category_id,
    price,
    oldPrice,
    img1,
    img2,
    badge,
    description: p.description,
    additional_information: p.additional_information,
    status: p.status,
    images: p.images || [],
  };
};

const getCategoryIdsToFilter = (catId, categories) => {
  if (!catId || catId === "All") return [];
  const ids = [catId];
  const addChildren = (parentId) => {
    const children = categories.filter((c) => c.parent_id === parentId);
    for (const child of children) {
      ids.push(child.category_id);
      addChildren(child.category_id);
    }
  };
  addChildren(catId);
  return ids;
};

export default function ShopPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get("category");

  const [allProducts, setAllProducts] = useState([]);
  const [apiCategories, setApiCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("default");
  const [page, setPage] = useState(1);
  const [hoveredCat, setHoveredCat] = useState(null);
  const perPage = 9;

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [prodRes, catRes] = await Promise.all([
          getALLproducts(),
          getcategorylist(),
        ]);
        const prodData = prodRes.data?.data || prodRes.data || [];
        const catData = catRes.data?.data || catRes.data || [];
        setAllProducts(prodData.map(mapApiProduct));
        setApiCategories(catData);
      } catch (err) {
        console.error("Failed to load shop data:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [categoryParam]);

  let filtered = [...allProducts];
  if (categoryParam && categoryParam !== "All") {
    const allowedIds = getCategoryIdsToFilter(categoryParam, apiCategories);
    filtered = allProducts.filter((p) => allowedIds.includes(p.category_id));
  }

  if (sort === "price-asc") filtered.sort((a, b) => a.price - b.price);
  if (sort === "price-desc") filtered.sort((a, b) => b.price - a.price);

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  // Show only main (parent) categories in the sidebar list
  const parentCats = apiCategories.filter((c) => !c.parent_id);

  // Get subcategories for a given parent, revealed on hover
  const getSubcategories = (parentId) =>
    apiCategories.filter((c) => c.parent_id === parentId);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-[#CAE4C9]" />
      </div>
    );
  }
  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-500" />
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb */}
      <div
        className="w-full h-32 bg-cover bg-center relative flex items-center p-8 border-b border-gray-100 overflow-hidden"
        style={{ backgroundImage: "url(/img/bg/banner18.jpg)" }}
      >
        <nav className="text-xs text-gray-400 font-medium tracking-wide justify-center items-center m-auto text-center">
          <h2 className="text-2xl text-black font-semibold mb-2">Products</h2>
          <Link to="/" className="hover:text-[#43a047] transition-colors">
            Home
          </Link>
          <span className="mx-2 text-gray-300">/</span>
          <span className="text-gray-600">Product</span>
        </nav>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="mb-8">
              <h3 className="font-bold text-dark mb-4 pb-2 border-b border-border-color uppercase text-sm tracking-wider">
                Categories
              </h3>
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => setSearchParams({})}
                    className={`text-sm w-full text-left py-1 hover:text-primary transition-colors ${!categoryParam ? "text-primary font-semibold" : "text-gray-500"}`}
                  >
                    All{" "}
                    <span className="text-gray-400">
                      ({allProducts.length})
                    </span>
                  </button>
                </li>
                {parentCats.map((c) => {
                  const subs = getSubcategories(c.category_id);
                  const hasSubs = subs.length > 0;
                  const isActive = hoveredCat === c.category_id;
                  const allowedIds = getCategoryIdsToFilter(
                    c.category_id,
                    apiCategories,
                  );
                  const count = allProducts.filter((p) =>
                    allowedIds.includes(p.category_id),
                  ).length;
                  return (
                    <li
                      key={c.category_id}
                      onMouseEnter={() => setHoveredCat(c.category_id)}
                      onMouseLeave={() => setHoveredCat(null)}
                    >
                      <button
                        onClick={() =>
                          setSearchParams({ category: c.category_id })
                        }
                        className={`flex items-center justify-between text-sm w-full text-left py-1 hover:text-primary transition-colors capitalize ${categoryParam === c.category_id ? "text-primary font-semibold" : "text-gray-500"}`}
                      >
                        <span>
                          {c.name}{" "}
                          <span className="text-gray-400">({count})</span>
                        </span>
                        {hasSubs && (
                          <FaChevronRight
                            size={8}
                            className={`text-gray-400 transition-transform duration-150 ${isActive ? "rotate-90" : ""}`}
                          />
                        )}
                      </button>

                      {/* Subcategories: always rendered, animated open/closed to avoid blink */}
                      {hasSubs && (
                        <div
                          className={`overflow-hidden transition-all duration-300 ease-in-out ${
                            isActive
                              ? "max-h-96 opacity-100"
                              : "max-h-0 opacity-0"
                          }`}
                        >
                          <ul className="mt-1 ml-3 pl-2 border-l border-gray-200 space-y-1">
                            {subs.map((sub) => {
                              const subAllowedIds = getCategoryIdsToFilter(
                                sub.category_id,
                                apiCategories,
                              );
                              const subCount = allProducts.filter((p) =>
                                subAllowedIds.includes(p.category_id),
                              ).length;
                              return (
                                <li key={sub.category_id}>
                                  <button
                                    onClick={() =>
                                      setSearchParams({
                                        category: sub.category_id,
                                      })
                                    }
                                    className={`text-xs w-full text-left py-1 hover:text-primary transition-colors capitalize ${categoryParam === sub.category_id ? "text-primary font-semibold" : "text-gray-400"}`}
                                  >
                                    {sub.name}{" "}
                                    <span className="text-gray-400">
                                      ({subCount})
                                    </span>
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* <div className="mb-8">
              <h3 className="font-bold text-dark mb-4 pb-2 border-b border-border-color uppercase text-sm tracking-wider">Price Range</h3>
              <input type="range" min="0" max="100" className="w-full accent-primary" />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>$0</span><span>$100</span>
              </div>
            </div> */}

            {/* <div>
              <h3 className="font-bold text-dark mb-4 pb-2 border-b border-border-color uppercase text-sm tracking-wider">Top Rated</h3>
              <div className="space-y-3">
                {allProducts.slice(0, 3).map(p => (
                  <div key={p.id} className="flex gap-2 items-center">
                    <img src={p.img1} alt={p.name} className="w-12 h-12 object-cover border border-border-color" />
                    <div>
                      <p className="text-xs font-medium hover:text-primary cursor-pointer">
                        <Link to={`/product-details/${p.product_id}`}>{p.name}</Link>
                      </p>
                      <p className="text-primary text-xs font-bold mt-0.5">${p.price}.00</p>
                    </div>
                  </div>
                ))}
              </div>
            </div> */}
          </aside>

          {/* Main */}
          <div className="flex-1">
            {/* Toolbar */}
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-border-color">
              <p className="text-sm text-gray-500">
                {filtered.length} products found
              </p>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="text-sm border border-border-color px-3 py-1.5 outline-none focus:border-primary"
              >
                <option value="default">Default Sorting</option>
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
              </select>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {paginated.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-10">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (n) => (
                    <button
                      key={n}
                      onClick={() => setPage(n)}
                      className={`w-9 h-9 text-sm font-medium border transition-colors ${
                        page === n
                          ? "bg-primary text-white border-primary"
                          : "border-border-color text-dark hover:bg-primary hover:text-white hover:border-primary"
                      }`}
                    >
                      {n}
                    </button>
                  ),
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}